import { TabContext, TabList, TabPanel } from "@mui/lab"
import {
    Badge,
    Box,
    Stack,
    Tab,
    Typography,
    styled,
    useTheme,
} from "@mui/material"
import TextField from "@mui/material/TextField"
import { useState, ReactElement, SyntheticEvent, ChangeEvent } from "react"

import AppTooltip from "../../components/helper/appTooltip"
import HelpIcon from "../../components/helper/helpIcon"
import AppControlledSelect from "../../components/inputs/controlled/appControlledSelect"
import AppPaper from "../../components/surfaces/appPaper"
import ActiveUserResults from "../../components/tables/adminPortal/activeUsers"
import PendingUserResults from "../../components/tables/adminPortal/pendingUsers"
import SystemAdminUsersResults from "../../components/tables/adminPortal/sysAdminUsers"
import {
    DNO_ADMIN,
    IDENTIFIER_TYPE_DNO_TAG,
    IDENTIFIER_TYPE_INSTALLER_TAG,
    IDENTIFIER_TYPE_LCT_MANU_TAG,
    INSTALLER_ADMIN,
    LCT_MANU_ADMIN,
    ENA_SYS_ADMIN,
} from "../../constants/roles"
import {
    TT_ACTIVE_USERS,
    TT_PENDING_USERS,
    TT_USER_MANAGE_PORTAL,
} from "../../constants/tooltips"
import { useGetUserData } from "../../hooks/useGetUserData"
import useActiveUsers from "../../services/middleware/hooks/adminPortal/useActiveUsers"
import { useIdentifierGroups } from "../../services/middleware/hooks/adminPortal/useIdentifierGroups"
import {
    BUTTON_RADIUS_MULTIPLIER,
    GRID_SPACING,
} from "../../themes/defaultThemeParams"

export const AppTab = styled(Tab)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius * BUTTON_RADIUS_MULTIPLIER,
    textTransform: "none",
}))

const APPROVALS = "APPROVALS"

/**
 * Webpage for Admin Portal
 */
const AdminPortal = (): ReactElement => {
    const theme = useTheme()
    const { isDnoAdmin, isInstallerAdmin, isManuAdmin, isEnaSysAdmin } =
        useGetUserData()

    const { groups, isLoading: isIDGroupLoading } = useIdentifierGroups()
    const [filter, setFilter] = useState<string | null>("ID:DNO:UKPN")
    const [search, setSearch] = useState("")
    const { users: pendingUsers } = useActiveUsers(false, filter, search)

    const [tabSelection, settabSelection] = useState(APPROVALS)

    const handleChange = (
        _event: SyntheticEvent,
        newTabSelection: string
    ): void => {
        settabSelection(newTabSelection)
    }

    const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => {
        setSearch(e.target.value)
    }

    const sysAdminSelectIdentifierGroup = (
        e_: SyntheticEvent<Element, Event>,
        nv: string | number | null
    ): void => {
        const newValue = nv as string | null
        setFilter(newValue)
        if (tabSelection !== APPROVALS) {
            if (newValue?.startsWith(IDENTIFIER_TYPE_DNO_TAG)) {
                settabSelection(DNO_ADMIN)
            }
            if (newValue?.startsWith(IDENTIFIER_TYPE_INSTALLER_TAG)) {
                settabSelection(INSTALLER_ADMIN)
            }
            if (newValue?.startsWith(IDENTIFIER_TYPE_LCT_MANU_TAG)) {
                settabSelection(LCT_MANU_ADMIN)
            }
        }
        if (newValue === ENA_SYS_ADMIN) {
            settabSelection(ENA_SYS_ADMIN)
        }
    }
    // ENA-SYS-ADMIN
    const showDnoActiveUsers =
        isDnoAdmin ||
        (isEnaSysAdmin && !!filter?.startsWith(IDENTIFIER_TYPE_DNO_TAG))

    const showInstallerActiveUsers =
        isInstallerAdmin ||
        (isEnaSysAdmin && !!filter?.startsWith(IDENTIFIER_TYPE_INSTALLER_TAG))

    const showManuActiveUsers =
        isManuAdmin ||
        (isEnaSysAdmin && !!filter?.startsWith(IDENTIFIER_TYPE_LCT_MANU_TAG))

    const showSysAdminActiveUsers = isEnaSysAdmin && filter === ENA_SYS_ADMIN

    return (
        <Stack
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minWidth: "100%",
            }}
        >
            <Stack
                direction={{ md: "column", lg: "row" }}
                justifyContent="space-between"
                gap={2}
                spacing={GRID_SPACING}
                marginBottom={GRID_SPACING}
            >
                <AppPaper
                    sx={{
                        height: "50px",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="h6" component="div">
                        <Box
                            color={theme.palette.primary.main}
                            display="inline"
                        >
                            User Management Portal
                        </Box>
                    </Typography>
                    <HelpIcon tooltip={TT_USER_MANAGE_PORTAL} />
                </AppPaper>

                <Stack
                    direction={{ md: "row", xs: "column" }}
                    gap={GRID_SPACING}
                    width={{ lg: "50%", md: "100%" }}
                >
                    <TextField
                        placeholder="Search by name..."
                        label="User search"
                        value={search}
                        onChange={handleSearch}
                    />
                    {isEnaSysAdmin && (
                        <AppControlledSelect
                            value={filter}
                            options={groups}
                            disabledClearable
                            label="Select the company you wish to view"
                            setValue={sysAdminSelectIdentifierGroup}
                            loading={isIDGroupLoading}
                            fullWidth
                        />
                    )}
                </Stack>
            </Stack>

            <TabContext value={tabSelection}>
                <TabList
                    aria-label="Admin Tabs"
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {!showSysAdminActiveUsers && (
                        <AppTab
                            label={
                                <AppTooltip title={TT_PENDING_USERS}>
                                    {!!pendingUsers &&
                                    pendingUsers?.length >= 1 ? (
                                        <Badge
                                            badgeContent={pendingUsers.length}
                                            color="error"
                                            max={100}
                                        >
                                            <Typography
                                                variant="body2"
                                                fontWeight="500"
                                            >
                                                Pending Users
                                            </Typography>
                                        </Badge>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            fontWeight="500"
                                        >
                                            Pending Users
                                        </Typography>
                                    )}
                                </AppTooltip>
                            }
                            value={APPROVALS}
                        />
                    )}

                    {showDnoActiveUsers && (
                        <AppTab
                            label={
                                <AppTooltip title={TT_ACTIVE_USERS}>
                                    <Typography
                                        variant="body2"
                                        fontWeight="500"
                                    >
                                        DNO Active Users
                                    </Typography>
                                </AppTooltip>
                            }
                            value={DNO_ADMIN}
                            disabled={false}
                        />
                    )}

                    {showInstallerActiveUsers && (
                        <AppTab
                            label={
                                <AppTooltip title={TT_ACTIVE_USERS}>
                                    <Typography
                                        variant="body2"
                                        fontWeight="500"
                                    >
                                        Installer Active Users
                                    </Typography>
                                </AppTooltip>
                            }
                            value={INSTALLER_ADMIN}
                        />
                    )}
                    {showManuActiveUsers && (
                        <AppTab
                            label={
                                <AppTooltip title={TT_ACTIVE_USERS}>
                                    <Typography
                                        variant="body2"
                                        fontWeight="500"
                                    >
                                        Manufacturer Active Users
                                    </Typography>
                                </AppTooltip>
                            }
                            value={LCT_MANU_ADMIN}
                        />
                    )}
                    {showSysAdminActiveUsers && (
                        <AppTab
                            label={
                                <AppTooltip title={TT_ACTIVE_USERS}>
                                    <Typography
                                        variant="body2"
                                        fontWeight="500"
                                    >
                                        System Admin Users
                                    </Typography>
                                </AppTooltip>
                            }
                            value={ENA_SYS_ADMIN}
                        />
                    )}
                </TabList>
                {!showSysAdminActiveUsers && (
                    <TabPanel value={APPROVALS}>
                        <PendingUserResults filter={filter} search={search} />
                    </TabPanel>
                )}

                <TabPanel value={DNO_ADMIN}>
                    <ActiveUserResults
                        filter={filter}
                        search={search}
                        mode={DNO_ADMIN}
                    />
                </TabPanel>
                <TabPanel value={INSTALLER_ADMIN}>
                    <ActiveUserResults
                        filter={filter}
                        search={search}
                        mode={INSTALLER_ADMIN}
                    />
                </TabPanel>
                <TabPanel value={LCT_MANU_ADMIN}>
                    <ActiveUserResults
                        filter={filter}
                        search={search}
                        mode={LCT_MANU_ADMIN}
                    />
                </TabPanel>
                <TabPanel value={ENA_SYS_ADMIN}>
                    <SystemAdminUsersResults
                        filter={filter}
                        search={search}
                        mode={ENA_SYS_ADMIN}
                    />
                </TabPanel>
            </TabContext>
        </Stack>
    )
}
export default AdminPortal
